/* eslint-disable react/react-in-jsx-scope */
import { useMemo, useState, useRef, useEffect } from 'react'

/**
 * Hook de exibição de caixas de diálogo.
 * Retorna a função open() que pode ser chamada para instanciar um modal e aguardar o resultado gerado ao fechar o modal.
 * O componente recebe as suas propriedades além das definidas em Modal.
 * As props do Modal devem ser mescladas com as customizadas pelo componente e passadas ao <Modal>, ex:
 *
 * const ModalExemplo = ({ title, onHide, closeButton, titleAlign, ...rest }) => (
 *   <Modal onHide={onHide} {...rest}>
 *     <Modal.Header closeButton={closeButton}>
 *       <Modal.Title align={titleAlign}>{title}</Modal.Title>
 *     </Modal.Header>
 *     <Modal.Body>Exemplo de conteúdo</Modal.Body>
 *     <Modal.Footer align="end">
 *       <Button onClick={() => onHide?.(false)}>Fechar</Button>
 *     </Modal.Footer>
 *   </Modal>
 * )
 *
 * Esse exemplo de componente pode ser então instanciado com o open():
 *
 * const [modal, open, close] = useModal(ModalExemplo)
 *
 * return (
 *  <>
 *    {modal}
 *    <Button onClick={() => open({title: 'Título do modal'})}>
 *      Abrir Modal
 *    </Button>
 *  </>
 * )
 *
 * É possível fechar o popup usando a função close() retornada pelo useModal():
 *
 * return (
 *  <>
 *    {modal}
 *    <Button onClick={() => close()}>
 *      Fechar Modal
 *    </Button>
 *  </>
 * )
 */
export function useModal(ModalComponent) {
  const promiseRef = useRef()
  const componentRef = useRef()
  const [show, setShow] = useState(false)
  const [{ onExited, ...componentProps }, setComponentProps] = useState({})

  useEffect(() => {
    if (!show) {
      setTimeout(() => onExited?.(), 300)
    }
  }, [show, onExited])

  const close = (res) => {
    setShow(false)
    promiseRef.current?.resolve(res)
  }

  const handleOnHide = (event) => {
    setShow(false)
    promiseRef.current?.resolve(event)
  }

  const handleOnShow = () => {
    setShow(true)
  }

  const open = useMemo(() => {
    return (props) => {
      componentRef.current = ModalComponent
      setComponentProps(props || {})
      setShow(true)
      return new Promise((resolve, reject) => {
        promiseRef.current = { resolve, reject }
      })
    }
  }, [ModalComponent])

  const Component = componentRef.current ?? (() => <></>)

  const modal = useMemo(
    () => (
      <Component
        show={show}
        onHide={handleOnHide}
        onShow={handleOnShow}
        {...componentProps}
      />
    ),
    [componentProps, show]
  )

  return [modal, open, close]
}
